import {Button, MenuItem, Paper} from '@material-ui/core';
import {Async, Loaded, toEither} from 'Core/models/Async';
import {filter} from 'fp-ts/lib/Array';
import * as E from 'fp-ts/lib/Either';
import {constTrue, identity, not} from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {Lens} from 'monocle-ts';
import React, {useEffect} from 'react';
import {connect} from 'react-redux';
import {RouteComponentProps, withRouter} from 'react-router';
import {ReducerState} from 'rootReducer';
import OutlinedSelect from 'Shared/components/OutlinedSelect';
import {renderAsync} from 'Shared/components/RenderAsync';
import RequiredTextInput from 'Shared/components/RequiredTextInput';
import ValidatableEmailInput from 'Shared/components/ValidatableEmailInput';
import ValidatablePhoneInput from 'Shared/components/ValidatablePhoneInput';
import ValidatableTextInput from 'Shared/components/ValidatableTextInput';
import {NoOp} from 'Shared/utilities/IOUtilities';
import * as FieldVicePresidentActions from 'Users/ducks/fieldVicePresidentUser';
import {getFullName} from 'Users/models/Agent';
import {AvailableFieldSalesLeaderPayload} from 'Users/models/AvailableFieldSalesLeaderPayload';
import {
  eqFieldVicePresident,
  ErrorFieldVicePresident,
  FieldVicePresident,
  matchFieldVicePresident,
  ValidFieldVicePresident,
} from 'Users/models/FieldVicePresident';
import {UserRouteConstants} from 'Users/Routes';
import './FieldVicePresidentEntry.sass';

type FieldVicePresidentProps = typeof FieldVicePresidentActions &
  ReturnType<typeof mapStateToProps> &
  RouteComponentProps<{userId: string}>;

const fieldVicePresidentLens = Lens.fromProp<FieldVicePresident>();

const loadFieldVicePresidentIfNeeded = (asyncFvp: Async<FieldVicePresident>, getUser: (userId: string) => void) =>
  pipe(
    toEither(asyncFvp),
    E.fold(
      () => NoOp,
      user =>
        matchFieldVicePresident(user, {
          ErrorFieldVicePresident: () => NoOp,
          InvalidFieldVicePresident: () => NoOp,
          SavedFieldVicePresident: () => NoOp,
          UnvalidatedFieldVicePresident: () => getUser,
          ValidFieldVicePresident: () => NoOp,
        })
    )
  );

export const FieldVicePresidentEntry = (props: FieldVicePresidentProps) => {
  const fieldVicePresidentUserId = O.fromNullable(props.match.params.userId);
  const isEdit = O.isSome(fieldVicePresidentUserId);
  useEffect(() => {
    pipe(
      fieldVicePresidentUserId,
      O.fold(NoOp, loadFieldVicePresidentIfNeeded(props.fieldVicePresidentUser, props.getFvpUser))
    );

    return () => {
      props.clearFieldVicePresident();
    };
  }, []);

  useEffect(() => {
    props.getRegions();
    props.getAvailableFSLS();
  }, []);

  const changeMapping = (fieldVicePresidentUser: FieldVicePresident) => {
    props.changeFieldVicePresidentUser(
      new Loaded(
        ValidFieldVicePresident.create(
          fieldVicePresidentUser.firstName,
          fieldVicePresidentUser.lastName,
          fieldVicePresidentUser.username,
          fieldVicePresidentUser.phoneNumber,
          fieldVicePresidentUser.email,
          fieldVicePresidentUser.fieldSalesLeaders
        )
      )
    );
  };

  const renderFVPForm = (fvp: FieldVicePresident) => (original: O.Option<FieldVicePresident>) => (
    fieldSalesLeaders: AvailableFieldSalesLeaderPayload[]
  ) => {
    const equalsCurrentFvp = (otherFvp: FieldVicePresident) => eqFieldVicePresident.equals(fvp, otherFvp);
    const hasFvpChanged = pipe(original, O.fold(constTrue, not(equalsCurrentFvp)));
    const allowSave = !(fvp instanceof ValidFieldVicePresident) || !hasFvpChanged;
    const methodLabel = pipe(
      fieldVicePresidentUserId,
      O.fold(
        () => 'Add',
        () => 'Edit'
      )
    );

    return (
      <Paper className="field-vice-president-entry">
        <h2 className="field-vice-president-entry__title">
          {methodLabel}
          &nbsp;Field Vice President
        </h2>
        <p className="field-vice-president-entry__description">
          Please enter the Field Vice President user's information below.
        </p>
        <form className="field-vice-president-entry__form">
          <RequiredTextInput
            label="First Name"
            className="field-vice-president-entry__field"
            onChange={firstName => changeMapping(fieldVicePresidentLens('firstName').set(firstName)(fvp))}
            value={fvp.firstName}
          />
          <RequiredTextInput
            label="Last Name"
            className="field-vice-president-entry__field"
            onChange={lastName => changeMapping(fieldVicePresidentLens('lastName').set(lastName)(fvp))}
            value={fvp.lastName}
          />
          <ValidatableTextInput
            label="User Name"
            disabled={isEdit}
            className="field-vice-president-entry__field"
            onValidate={props.validateUsername}
            value={fvp.username}
          />
          <ValidatableEmailInput
            className="field-vice-president-entry__field"
            value={fvp.email}
            label="Email"
            onValidate={props.validateEmail}
          />
          <ValidatablePhoneInput
            label="Phone Number"
            className="field-vice-president-entry__field"
            onValidate={props.validatePhone}
            value={fvp.phoneNumber}
          />
          {O.isSome(fieldVicePresidentUserId) && (
            <OutlinedSelect
              value={fvp.fieldSalesLeaders.map(region => region.id)}
              className="field-vice-president-entry__field"
              label="Field Sales Leaders"
              multiple
              onChange={event => {
                const currentRegionIds = (event.target.value as unknown) as string[];

                const fsls = pipe(
                  props.availableFsls,
                  toEither,
                  E.fold(
                    () => [],
                    filter(fsl => currentRegionIds.includes(fsl.id))
                  )
                );

                changeMapping(fieldVicePresidentLens('fieldSalesLeaders').set(fsls)(fvp));
              }}
            >
              {fieldSalesLeaders.map(fsl => (
                <MenuItem value={fsl.id} key={fsl.id}>
                  {getFullName(fsl)}
                </MenuItem>
              ))}
            </OutlinedSelect>
          )}
          <div className="field-vice-president-entry-form__buttons">
            <div className="field-vice-president-entry-form__return-button">
              <Button
                variant="contained"
                onClick={() => {
                  props.history.push(UserRouteConstants.USERS);
                }}
              >
                Cancel
              </Button>
            </div>
            <Button
              className="field-vice-president-entry-form__add-button"
              color="primary"
              variant="contained"
              disabled={allowSave}
              onClick={() => {
                props.saveFieldVicePresidentUser(
                  fvp as ValidFieldVicePresident,
                  pipe(
                    fieldVicePresidentUserId,
                    O.fold(() => '', identity)
                  )
                );
              }}
            >
              Save
            </Button>
          </div>
          <p className="agent-entry-form__error-message">
            {fvp instanceof ErrorFieldVicePresident && fvp.errors.message}
          </p>
        </form>
      </Paper>
    );
  };

  const eitherFvp = toEither(props.fieldVicePresidentUser);
  const originalFvpOption = pipe(
    props.original,
    O.fold(
      () => O.none,
      original => O.fromEither(toEither(original))
    )
  );

  return pipe(
    E.right(renderFVPForm),
    E.ap(eitherFvp),
    E.ap(E.right(originalFvpOption)),
    E.ap(toEither(props.availableFsls)),
    E.fold(renderAsync, identity)
  );
};

const mapStateToProps = (state: ReducerState) => ({
  fieldVicePresidentUser: state.user.fieldVicePresidentUser.fieldVicePresident,
  original: state.user.fieldVicePresidentUser.original,
  regions: state.user.fieldVicePresidentUser.regions,
  availableFsls: state.user.fieldVicePresidentUser.availableFsls,
  userClaims: state.user.profile.userClaims,
});

export default withRouter(
  connect(mapStateToProps, {
    ...FieldVicePresidentActions,
  })(FieldVicePresidentEntry)
);
